/**
 * escapeShellDollars
 *
 * 把"看起来不是 LaTeX 公式分隔符"的 $ 字符做安全处理，避免 KaTeX /
 * markdown-it-katex 把诸如 `echo $PATH`、`$(date)`、`${VAR}`、`价格 $100`
 * 之类内容当成行内公式吃掉。
 *
 * 两种 mode：
 *   - mode='markdown'（默认，给 markdown-it 用）
 *       输出 `\$`。markdown-it 的 escape 规则在 markdown-it-katex 的 math_inline
 *       之前运行，会把 `\$` 消解为字面 `$`，最终 HTML 没有任何额外字符。
 *
 *   - mode='html'（给 v-latex 这种"先 innerHTML 再让 KaTeX auto-render 扫描"的
 *     场景用）
 *       输出 `<span class="vlsd">$</span>`。KaTeX 的 renderElem 只合并相邻
 *       的 *text* 兄弟节点（见 katex/contrib/auto-render/auto-render.ts），
 *       中间一插入 element 就会切断 text node 串，KaTeX 的 splitAtDelimiters
 *       从而无法把这个 $ 与其它 $ 配成对。浏览器照常显示 $ 字符，没有视觉副作用。
 *       注意：v-latex 指令需要把 'vlsd' 加入 ignoredClasses 做防御性兜底。
 *       直接写 `\$` 不行，因为 innerHTML 不解析 \ 转义、且 KaTeX 在寻找
 *       *开启* 分隔符时不检查前导 \，反而会让用户看到字面 "\$"。
 *
 * 设计原则（保守，倾向"不渲染"）：
 *  1. 代码区（<pre>/<code>/反引号/围栏代码块）以及 HTML 标签内部的 $ 一律不动；
 *  2. 已经写成 \$ 的转义 $ 一律不动；
 *  3. 双美元 $$...$$ 优先按块级公式判定：能配对、且内部含 LaTeX 特征字符才保留；
 *  4. 单美元 $...$ 优先按行内公式判定：能配对、且内部含 LaTeX 特征字符才保留；
 *     行内配对允许跨过单个软换行（AI 流式输出 / Word 粘贴常把公式拆到下一行），
 *     但遇到连续两次 \n（段落分隔）立即停手；
 *  5. 若已配对但无 LaTeX 特征，再看内部是否是"单字母数学变量"形态——也就是
 *     一个英文字母，可选再接 1 个数字或 1 个撇号（如 $P$、$V$、$x$、$x1$、$F'$）
 *     就按公式保留。范围故意收得很窄，避免误伤 $HOME$、$PATH$ 等多字母 Shell
 *     变量名，以及 $HOME/bin:$PATH 这种路径形态；
 *  6. 否则——若紧跟 Shell 触发字符（[A-Za-z_({0-9?#@*$!-]）→ 转义当前 $；
 *  7. 否则——若已配对但内部无特征 → 把这对 $ 一并转义；
 *  8. 否则——落单的 $ → 转义。
 *
 * "LaTeX 特征字符"指 \、^、_、{、} 或常见 macro（\frac、\sqrt、希腊字母等）。
 * 真公式几乎必带这些字符；不含特征的成对 $...$ 在历史题库里多半是
 * "价格 $100, 优惠 $200" 之类的误用，但 $P$ 这种单变量也是高频公式形态，
 * 故再加一层"短而干净"的豁免去把它们从误判里救回来。
 */

export type EscapeMode = 'markdown' | 'html'

const LATEX_FEATURE_RE = /[\\^_{}]|\\(?:frac|sqrt|sum|int|prod|lim|cdot|times|div|pm|mp|approx|leq|geq|neq|infty|alpha|beta|gamma|delta|epsilon|varepsilon|zeta|eta|theta|vartheta|iota|kappa|lambda|mu|nu|xi|pi|rho|sigma|tau|upsilon|phi|varphi|chi|psi|omega|Gamma|Delta|Theta|Lambda|Xi|Pi|Sigma|Phi|Psi|Omega|hat|bar|tilde|vec|dot|ddot|left|right|begin|end|mathbf|mathrm|mathcal|mathbb|text|operatorname|displaystyle|partial|nabla|forall|exists|in|notin|subset|supset|cup|cap)\b/

const SHELL_TRIGGER_RE = /[A-Za-z_({0-9?#@*$!\-]/

// 只豁免最典型的"数学单变量"形态：单个英文字母，可选再接 1 个数字或 1 个撇号。
// 覆盖 $P$、$V$、$G$、$x$、$y$、$F'$、$x1$ 这种被 SHELL_TRIGGER 误伤的常见公式。
//
// 故意写得很窄：先前更宽的版本（白名单含 `/` `:` `\s`、长度允许到 12）会把
// `$HOME/bin:$PATH` 里 `HOME/bin:` 当成公式、把 `$HOME$` `$PATH$` `$USER$` 这种
// 全大写 Shell 变量名当成公式，严重误伤包含 Shell 命令的 markdown 段落。
// $x=1$、$f(x)$ 这种没有 LaTeX 特征的"裸表达式"在历史 AI 输出里也不常见
// （AI 通常会写成 $x = 1$ 或加宏，含空格/反斜杠就会被 LATEX_FEATURE 接走），
// 这里宁可保守不救它们，也不能把 Shell 命令吞掉。
const SIMPLE_MATH_INNER_RE = /^[A-Za-z](?:[0-9]|')?$/

/** v-latex 指令应把这个 class 加入 KaTeX 的 ignoredClasses 列表 */
export const SKIP_DOLLAR_CLASS = 'vlsd'

const HTML_DOLLAR_OPEN = `<span class="${SKIP_DOLLAR_CLASS}">`
const HTML_DOLLAR_CLOSE = '</span>'

const PLACEHOLDER_PREFIX = '\u0000ESDOL'
const PLACEHOLDER_SUFFIX = '\u0000'
const PLACEHOLDER_RE = /\u0000ESDOL(\d+)\u0000/g

function escapedDollar(mode: EscapeMode): string {
  return mode === 'html' ? `${HTML_DOLLAR_OPEN}$${HTML_DOLLAR_CLOSE}` : '\\$'
}

function isEscapedDollar(text: string, idx: number): boolean {
  let count = 0
  for (let p = idx - 1; p >= 0 && text[p] === '\\'; p--) count++
  return count % 2 === 1
}

function findInlinePair(text: string, from: number): number {
  // 允许行内 $...$ 跨过单个软换行（AI 流式输出 / Word 粘贴常见把公式拆到下一行），
  // 但只要出现"连续 \n"（段落分隔）就立刻停手，避免吞掉整段非公式正文。
  let sawNewline = false
  for (let k = from; k < text.length; k++) {
    const c = text[k]
    if (c === '\n') {
      if (sawNewline) return -1
      if (text[k + 1] === '\n') return -1
      sawNewline = true
      continue
    }
    if (c === '$') {
      if (isEscapedDollar(text, k)) continue
      // 紧贴另一个 $ 视为块级，不在行内配对
      if (text[k + 1] === '$') return -1
      return k
    }
  }
  return -1
}

function findBlockClosing(text: string, from: number): number {
  for (let k = from; k <= text.length - 2; k++) {
    if (text[k] === '$' && text[k + 1] === '$') {
      if (!isEscapedDollar(text, k)) return k
    }
  }
  return -1
}

function maskCodeRegions(input: string): { masked: string; masks: string[] } {
  const masks: string[] = []
  const place = (m: string) => {
    const i = masks.length
    masks.push(m)
    return `${PLACEHOLDER_PREFIX}${i}${PLACEHOLDER_SUFFIX}`
  }

  let masked = input
  // 顺序敏感：先吃围栏/块级，再吃行内/标签
  masked = masked.replace(/```[\s\S]*?```/g, place)
  masked = masked.replace(/<pre\b[^>]*>[\s\S]*?<\/pre>/gi, place)
  masked = masked.replace(/<code\b[^>]*>[\s\S]*?<\/code>/gi, place)
  masked = masked.replace(/`[^`\n]+`/g, place)
  // 屏蔽剩余 HTML 标签本身（避免 <a href="?p=$1"> 这类 attribute 被改写）
  masked = masked.replace(/<[^<>]+>/g, place)

  return { masked, masks }
}

function unmask(text: string, masks: string[]): string {
  return text.replace(PLACEHOLDER_RE, (full, idx) => {
    const i = Number(idx)
    return Number.isFinite(i) && i < masks.length ? masks[i] : full
  })
}

export function escapeNonMathDollars(input: string, mode: EscapeMode = 'markdown'): string {
  if (!input) return ''
  if (input.indexOf('$') === -1) return input

  const ESC = escapedDollar(mode)

  const { masked, masks } = maskCodeRegions(input)
  const out: string[] = []
  let i = 0
  const len = masked.length

  while (i < len) {
    const ch = masked[i]
    if (ch !== '$') {
      out.push(ch)
      i++
      continue
    }

    if (isEscapedDollar(masked, i)) {
      out.push(ch)
      i++
      continue
    }

    // $$ 块级
    if (masked[i + 1] === '$') {
      const close = findBlockClosing(masked, i + 2)
      if (close === -1) {
        out.push(ESC, ESC)
        i += 2
        continue
      }
      const inner = masked.slice(i + 2, close)
      if (LATEX_FEATURE_RE.test(inner)) {
        out.push('$$', inner, '$$')
      } else {
        out.push(ESC, ESC, inner, ESC, ESC)
      }
      i = close + 2
      continue
    }

    // 单 $：先尝试行内配对
    const close = findInlinePair(masked, i + 1)
    if (close !== -1) {
      const inner = masked.slice(i + 1, close)
      if (LATEX_FEATURE_RE.test(inner)) {
        out.push('$', inner, '$')
        i = close + 1
        continue
      }
      // 配对成功但内部无 LaTeX 特征：先豁免"短且干净"的简单公式（$P$、$x=1$ 等）
      if (SIMPLE_MATH_INNER_RE.test(inner.trim())) {
        out.push('$', inner, '$')
        i = close + 1
        continue
      }
      const next = masked[i + 1]
      if (next && SHELL_TRIGGER_RE.test(next)) {
        // 形如 $X ... $Y：开头是 Shell 模式，只转义当前 $，留给后续 $ 自己判定
        out.push(ESC)
        i++
        continue
      }
      // 普通非公式配对（如 "价格 $100, 优惠 $200" 的某种形态）→ 一并转义
      out.push(ESC, inner, ESC)
      i = close + 1
      continue
    }

    // 落单的 $
    out.push(ESC)
    i++
  }

  return unmask(out.join(''), masks)
}

export default escapeNonMathDollars
